import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
    AiTaskDetails,
    AiTaskGenerateDescription,
    ProofreadEntityType,
    ProofreadTextInput,
    ProofreadTextOutput,
    TaskServiceProxy
} from '@shared/service-proxies/service-proxies';
import { AppConsts } from '@shared/commonHelper/AppConsts';

declare const abp: any;

/**
 * Event emitted by the streaming methods. Three shapes:
 *  - `{ delta: '...' }` — a new chunk of HTML to append to the editor.
 *  - `{ done: true }` — the stream completed successfully.
 *  - `{ isError: true, errorMessage: '...' }` — the stream failed.
 *
 * The overlay accumulates deltas as they arrive, then transitions to 'ready'
 * on `done`.
 */
export interface AiNotesStreamEvent {
    delta?: string;
    done?: boolean;
    isError?: boolean;
    errorMessage?: string;
}

/**
 * Input for the initial "Ask AI to write something" generation. When
 * `useNotesAsContext` is true the existing notes are passed alongside the
 * prompt so the model can transform them; otherwise the model writes from
 * scratch using only the prompt.
 */
export interface AiNotesGenerateInput {
    prompt: string;
    sourceNotesHtml: string;
    useNotesAsContext: boolean;
}

/**
 * Input for refinement on an already-generated draft. `currentDraftHtml` is
 * the LIVE editor content (after any hand-edits the user has made), so the
 * model operates on the most recent version rather than the original AI
 * output. The instruction is interpreted intelligently against the draft
 * (rewrite / add / remove / restructure / reformat).
 *
 * Optional context (carried from the originating callout):
 *  - `sourceNotesHtml` + `useNotesAsContext`: the user's existing notes the
 *    overlay was opened against. Passing these on every refine call lets the
 *    model cross-reference the original source content so its decisions stay
 *    grounded — e.g. "make it precise" should still reflect facts present in
 *    the original notes even if the current draft summarised them away.
 *  - `originalPrompt`: the initial Ask-AI prompt the user submitted. Helps
 *    the model understand the overall goal of the document, so refinements
 *    don't drift away from the original intent across multiple iterations.
 */
export interface AiNotesRefineInput {
    instruction: string;
    currentDraftHtml: string;
    sourceNotesHtml?: string;
    useNotesAsContext?: boolean;
    originalPrompt?: string;
}

/** Result returned by either generate or refine. */
export interface AiNotesResult {
    html: string;
    isError: boolean;
    errorMessage: string;
}

/**
 * The slice of `vf-tiptap-editor` an inline proofread drives. A structural type rather than the
 * component class, so this service does not import the editor - the editor's own Write-with-AI
 * toolbar already points back at this folder.
 */
export interface ProofreadableEditor {
    getHtml(): string;
    getSelectionRange(): { from: number; to: number } | null;
    expandRangeToSentences(from: number, to: number): { from: number; to: number };
    getRangeHtml(from: number, to: number): string;
    replaceRange(from: number, to: number, html: string): void;
    setValue(html: string): void;
    showProofreadProgress(): void;
    hideProofreadProgress(): void;
    showProofreadReview(beforeHtml: string): number;
}

const SEPARATOR = '\n\n---\n\n';

/**
 * Shared list-type guidance. Tells the model how to pick between the three
 * list variants supported by the Tiptap editor when the output includes
 * list items. Mirrored in the streaming endpoint's system prompt
 * (`AiStreamController.BuildMessageList`) so both paths produce the same
 * intent-driven list selection.
 */
const LIST_TYPE_GUIDANCE: string =
    'LIST TYPE SELECTION (when the output contains list items, pick the right list type):'
    + '\n- CHECKLIST: <ul data-type="taskList"> with <li data-type="taskItem" data-checked="false"><p>...</p></li> '
    + 'for ACTIONABLE items the user can check off (grocery list, to-do list, action items, packing list, follow-ups). '
    + 'Default data-checked="false" unless explicitly already done.'
    + '\n- NUMBERED LIST: <ol><li>...</li></ol> for ORDERED / RANKED items (steps, recipes, processes, priorities, '
    + 'rankings — anything with intrinsic sequence).'
    + '\n- BULLET LIST: <ul><li>...</li></ul> for UNORDERED items that are neither actionable nor ranked '
    + '(feature lists, key points, options, considerations, summaries).'
    + '\nDefault to a bullet list when the items don\'t clearly fit checklist or numbered. Don\'t mix list types '
    + 'inside the same logical group.';

@Injectable({ providedIn: 'root' })
export class AiNotesService {

    constructor(private _taskService: TaskServiceProxy) {}

    /**
     * Initial generation. Composes a single `userInput` string that the
     * existing `aiTaskGenerateDescription` endpoint accepts, embedding both
     * the source notes (when relevant) and the user's instruction so the
     * model has the full picture.
     */
    public generate(input: AiNotesGenerateInput): Observable<AiNotesResult> {
        let body: AiTaskGenerateDescription = new AiTaskGenerateDescription();
        let composedInput: string = '';

        if (input.useNotesAsContext && this._hasContent(input.sourceNotesHtml)) {
            composedInput = 'EXISTING NOTES (source content the user wants you to work with):'
                + SEPARATOR
                + input.sourceNotesHtml
                + SEPARATOR
                + 'INSTRUCTION (what to do with the notes above): '
                + input.prompt
                + SEPARATOR
                + 'Guidance: Treat the existing notes as source content. Produce output that satisfies the instruction. '
                + 'Preserve the original meaning unless the instruction says otherwise. Do not echo the instruction back. '
                + 'Return polished HTML suitable for direct insertion into a notes field.'
                + SEPARATOR
                + LIST_TYPE_GUIDANCE;
        } else {
            composedInput = 'INSTRUCTION: ' + input.prompt
                + SEPARATOR
                + 'Guidance: The user has not provided source content. Generate fresh content based on the instruction. '
                + 'Do not reference or fabricate "existing notes". Return polished HTML suitable for direct insertion into a notes field.'
                + SEPARATOR
                + LIST_TYPE_GUIDANCE;
        }

        body.userInput = composedInput;
        body.actionType = 1;
        body.lengthOption = 0;
        body.toneOption = 0;
        body.isAddChecklist = false;
        body.isSuggestTaskName = false;

        return this._taskService.aiTaskGenerateDescription(body).pipe(
            map(response => this._mapResponse(response))
        );
    }

    /**
     * Refine the current draft. The model is told to make the minimum change
     * required to satisfy the instruction — additions are inserted, removals
     * are surgical, rewrites preserve structure where possible. Unrelated
     * content is kept intact.
     *
     * When the overlay was opened with the "Use my notes as context" toggle
     * ON, the original source notes are folded into the prompt alongside the
     * current draft so the model can cross-reference facts and intent from
     * the source — keeping refinements grounded across multiple iterations
     * rather than drifting away from the original material.
     */
    public refine(input: AiNotesRefineInput): Observable<AiNotesResult> {
        let body: AiTaskGenerateDescription = new AiTaskGenerateDescription();
        let composedInput: string = '';
        let useNotes: boolean = !!input.useNotesAsContext && this._hasContent(input.sourceNotesHtml || '');
        let hasOriginalPrompt: boolean = !!(input.originalPrompt && input.originalPrompt.trim().length > 0);

        if (hasOriginalPrompt) {
            composedInput += 'ORIGINAL REQUEST (the user\'s initial goal for this document — keep refinements aligned with it):'
                + SEPARATOR
                + (input.originalPrompt || '')
                + SEPARATOR;
        }

        if (useNotes) {
            composedInput += 'SOURCE NOTES (the user\'s original notes the draft was built from — use as ground truth for facts):'
                + SEPARATOR
                + (input.sourceNotesHtml || '')
                + SEPARATOR;
        }

        composedInput += 'CURRENT DRAFT (the live content the user is iterating on — your changes go here):'
            + SEPARATOR
            + (input.currentDraftHtml || '')
            + SEPARATOR
            + 'REFINEMENT INSTRUCTION: ' + input.instruction
            + SEPARATOR
            + 'Guidance: Interpret the instruction intelligently against the current draft and decide whether to '
            + 'rewrite, add, remove, restructure, or reformat. Make the minimum change required to satisfy the '
            + 'instruction. Preserve structure, tone, and unrelated content unless the instruction asks otherwise. '
            + 'Respect any manual edits already present in the draft.';

        if (useNotes) {
            composedInput += ' When the instruction asks for facts, details, or clarifications, prefer information present in '
                + 'the SOURCE NOTES above. Do not invent facts the source does not support.';
        }
        if (hasOriginalPrompt) {
            composedInput += ' Keep the result aligned with the ORIGINAL REQUEST\'s overall goal — refinements should '
                + 'sharpen the draft toward that goal, not drift away from it.';
        }
        composedInput += ' Return polished HTML suitable for direct insertion into a notes field.';
        composedInput += SEPARATOR + LIST_TYPE_GUIDANCE;

        body.userInput = composedInput;
        body.actionType = 1;
        body.lengthOption = 0;
        body.toneOption = 0;
        body.isAddChecklist = false;
        body.isSuggestTaskName = false;

        return this._taskService.aiTaskGenerateDescription(body).pipe(
            map(response => this._mapResponse(response))
        );
    }

    /**
     * Inline proofread. Uses the dedicated, leaner `aiProofreadText` endpoint
     * (smaller system prompt + GPT-5 deployment) instead of the heavier
     * `aiTaskGenerateDescription` endpoint, which cuts latency significantly
     * — proofread is the hot path triggered every time the user runs the
     * inline review pill, so it benefits the most from a focused prompt.
     */
    public proofread(html: string): Observable<AiNotesResult> {
        let body: ProofreadTextInput = new ProofreadTextInput();
        body.title = '';
        body.description = html || '';
        body.type = ProofreadEntityType._2;
        return this._taskService.aiProofreadText(body).pipe(
            map(response => this._mapProofreadResponse(response))
        );
    }

    /**
     * Inline proofread of what an editor holds, the way the task Details notes do it
     * (`edit-task-from-template-modal.runInlineProofread`, `add-task-modal.runInlineProofread`): a
     * selection is widened to whole sentences and only that range is sent, otherwise the whole note;
     * the editor's own in-flight pill shows while the request runs; the result goes back through
     * `replaceRange` / `setValue`; then the editor's "N changes - Accept all / Undo all" pill opens
     * over a before-snapshot.
     *
     * Resolves with the editor's change count (0 = the text was already fine, so the caller can say
     * so), or -1 when there was nothing to send. On failure the in-flight pill is cleared and the
     * observable errors, so the caller can toast.
     *
     * Lives here so a surface that embeds the editor - the step drawer - runs exactly the notes
     * fields' flow. It used to be a host round-trip that wrote the AI's answer straight into the
     * field: correct text, but no progress, no review and no way back, which read as "Proofread
     * does nothing".
     */
    public proofreadInEditor(editor: ProofreadableEditor): Observable<number> {
        const beforeFull: string = editor.getHtml() || '';
        let range: { from: number; to: number } | null = editor.getSelectionRange();
        let target: string = '';
        let usingRange: boolean = false;
        if (range) {
            range = editor.expandRangeToSentences(range.from, range.to);
            target = editor.getRangeHtml(range.from, range.to);
            usingRange = true;
        } else {
            target = beforeFull;
        }
        if (!target || !target.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()) {
            return of(-1);
        }
        editor.showProofreadProgress();
        return this.proofread(target).pipe(
            map((result: AiNotesResult) => {
                if (result.isError) {
                    throw new Error(result.errorMessage || 'Could not proofread that just now.');
                }
                if (usingRange && range) {
                    editor.replaceRange(range.from, range.to, result.html || '');
                } else {
                    editor.setValue(result.html || '');
                }
                return editor.showProofreadReview(beforeFull);
            }),
            catchError(err => {
                editor.hideProofreadProgress();
                return throwError(err);
            })
        );
    }

    private _mapProofreadResponse(response: { isError?: boolean; errorMessage?: string; responseData?: ProofreadTextOutput }): AiNotesResult {
        let result: AiNotesResult = { html: '', isError: false, errorMessage: '' };
        if (!response) {
            result.isError = true;
            result.errorMessage = 'No response from AI service.';
            return result;
        }
        if (response.isError) {
            result.isError = true;
            result.errorMessage = response.errorMessage || 'Proofread request failed.';
            return result;
        }
        result.html = (response.responseData && response.responseData.description) || '';
        return result;
    }

    private _mapResponse(response: { isError?: boolean; errorMessage?: string; responseData?: AiTaskDetails }): AiNotesResult {
        let result: AiNotesResult = { html: '', isError: false, errorMessage: '' };
        if (!response) {
            result.isError = true;
            result.errorMessage = 'No response from AI service.';
            return result;
        }
        if (response.isError) {
            result.isError = true;
            result.errorMessage = response.errorMessage || 'AI request failed.';
            return result;
        }
        result.html = (response.responseData && response.responseData.description) || '';
        return result;
    }

    private _hasContent(html: string): boolean {
        if (!html) {
            return false;
        }
        let stripped: string = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        return stripped.length > 0;
    }

    // ── Streaming variants ──

    /**
     * Streaming version of `generate`. Returns an Observable that emits a
     * sequence of `{ delta: '...' }` events as Azure OpenAI tokens arrive,
     * followed by `{ done: true }` on success or `{ isError: true, ... }` on
     * failure. The overlay subscribes to this directly and pushes each delta
     * into the editor so the user sees content build up progressively rather
     * than waiting for the full response.
     */
    public generateStream(input: AiNotesGenerateInput): Observable<AiNotesStreamEvent> {
        let body: any = {
            prompt: input.prompt || '',
            sourceNotesHtml: input.sourceNotesHtml || '',
            useNotesAsContext: !!input.useNotesAsContext
        };
        return this._consumeSseStream('/api/AiStream/GenerateNotes', body);
    }

    /**
     * Streaming version of `refine`. Same context plumbing as the non-streaming
     * variant — passes the source notes, original prompt, and current draft so
     * the model has full context for the iteration. See `refine()` JSDoc.
     */
    public refineStream(input: AiNotesRefineInput): Observable<AiNotesStreamEvent> {
        let body: any = {
            instruction: input.instruction || '',
            currentDraftHtml: input.currentDraftHtml || '',
            sourceNotesHtml: input.sourceNotesHtml || '',
            useNotesAsContext: !!input.useNotesAsContext,
            originalPrompt: input.originalPrompt || ''
        };
        return this._consumeSseStream('/api/AiStream/RefineNotes', body);
    }

    /**
     * Shared SSE-reader. Posts to the given endpoint with the standard ABP
     * auth + tenant headers, then reads the response body as a stream, parses
     * each `data: { ... }` frame, and emits the delta back to the subscriber.
     * Stops on `data: [DONE]` or when the subscriber unsubscribes (the
     * abortController fires, cancelling the upstream Azure call too).
     */
    private _consumeSseStream(endpoint: string, body: any): Observable<AiNotesStreamEvent> {
        return new Observable<AiNotesStreamEvent>(subscriber => {
            let controller: AbortController = new AbortController();
            let url: string = (AppConsts.remoteServiceBaseUrl || '') + endpoint;
            let headers: { [key: string]: string } = {
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream'
            };
            let token: string = (typeof abp !== 'undefined' && abp.auth && typeof abp.auth.getToken === 'function')
                ? abp.auth.getToken()
                : null;
            if (token) {
                headers['Authorization'] = 'Bearer ' + token;
            }
            let tenantId: number | null = (typeof abp !== 'undefined' && abp.multiTenancy && typeof abp.multiTenancy.getTenantIdCookie === 'function')
                ? abp.multiTenancy.getTenantIdCookie()
                : null;
            if (tenantId !== null && tenantId !== undefined) {
                headers['Abp.TenantId'] = String(tenantId);
            }

            fetch(url, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(body),
                signal: controller.signal,
                credentials: 'include'
            }).then(async response => {
                if (!response.ok) {
                    // Try to surface the body so backend exceptions / ABP error
                    // envelopes are visible in the overlay's error banner.
                    let errBody: string = '';
                    try { errBody = await response.text(); } catch { /* ignore */ }
                    subscriber.next({
                        isError: true,
                        errorMessage: 'HTTP ' + response.status + ' ' + response.statusText
                            + (errBody ? ' — ' + errBody.substring(0, 400) : '')
                    });
                    subscriber.complete();
                    return;
                }
                if (!response.body) {
                    subscriber.next({ isError: true, errorMessage: 'Streaming not supported in this browser.' });
                    subscriber.complete();
                    return;
                }
                // If the response isn't event-stream, something upstream is
                // intercepting (proxy, ABP wrapper, etc.). Read it as text so
                // the diagnostic surfaces instead of getting silently parsed
                // as 'no SSE frames' and leaving the editor blank.
                let contentType: string = (response.headers.get('content-type') || '').toLowerCase();
                if (contentType.indexOf('event-stream') === -1) {
                    let body: string = '';
                    try { body = await response.text(); } catch { /* ignore */ }
                    subscriber.next({
                        isError: true,
                        errorMessage: 'Server returned non-streaming response (' + (contentType || 'unknown content-type')
                            + ')' + (body ? ': ' + body.substring(0, 400) : '')
                    });
                    subscriber.complete();
                    return;
                }
                let reader: ReadableStreamDefaultReader<Uint8Array> = response.body.getReader();
                let decoder: TextDecoder = new TextDecoder();
                let buffer: string = '';
                let aborted: boolean = false;
                let anyDelta: boolean = false;
                try {
                    while (true) {
                        let chunk = await reader.read();
                        if (chunk.done) {
                            break;
                        }
                        buffer += decoder.decode(chunk.value, { stream: true });
                        // SSE frames are separated by blank lines. Split on \n,
                        // process complete lines, keep the trailing partial in buffer.
                        let lines: string[] = buffer.split(/\r?\n/);
                        buffer = lines.pop() || '';
                        for (let i = 0; i < lines.length; i++) {
                            let line: string = lines[i];
                            if (!line || line.indexOf('data:') !== 0) {
                                continue;
                            }
                            let payload: string = line.substring(5).trim();
                            if (payload === '[DONE]') {
                                aborted = true;
                                break;
                            }
                            try {
                                let parsed: any = JSON.parse(payload);
                                if (parsed && parsed.error) {
                                    subscriber.next({ isError: true, errorMessage: parsed.error });
                                } else if (parsed && typeof parsed.delta === 'string') {
                                    anyDelta = true;
                                    subscriber.next({ delta: parsed.delta });
                                }
                            } catch {
                                // Skip malformed frame — the server normally never sends one.
                            }
                        }
                        if (aborted) {
                            break;
                        }
                    }
                    if (!anyDelta) {
                        // Stream completed cleanly but produced no content —
                        // surface this so the user doesn't get a blank editor
                        // with no explanation. Usually means the AI returned
                        // an empty completion (rare) or the deployment isn't
                        // honouring `stream: true`.
                        subscriber.next({
                            isError: true,
                            errorMessage: 'AI returned no content. The deployment may not support streaming, or the prompt was rejected.'
                        });
                    } else {
                        subscriber.next({ done: true });
                    }
                    subscriber.complete();
                } catch (readErr) {
                    if ((readErr as any) && (readErr as any).name === 'AbortError') {
                        subscriber.complete();
                        return;
                    }
                    subscriber.next({ isError: true, errorMessage: ((readErr as any) && (readErr as any).message) || 'Stream read failed.' });
                    subscriber.complete();
                } finally {
                    try { reader.releaseLock(); } catch { /* noop */ }
                }
            }).catch(err => {
                if (err && err.name === 'AbortError') {
                    subscriber.complete();
                    return;
                }
                subscriber.next({ isError: true, errorMessage: (err && err.message) || 'Stream request failed.' });
                subscriber.complete();
            });

            // Unsubscribe → abort the in-flight request, which propagates to the
            // server's cancellation token and stops the upstream Azure call.
            return () => {
                try { controller.abort(); } catch { /* noop */ }
            };
        });
    }
}
